<template>
  <el-dialog
    title="Guest Check-In"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
  >
    <div v-if="booking">
      <!-- Guest Information -->
      <el-card class="info-card">
        <div slot="header">
          <span>Guest Information</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <p><strong>Name:</strong> {{ booking.guest_name }}</p>
            <p><strong>Email:</strong> {{ booking.guest_email }}</p>
            <p><strong>Phone:</strong> {{ booking.guest_phone }}</p>
          </el-col>
          <el-col :span="12">
            <p><strong>Booking Code:</strong> {{ booking.booking_code }}</p>
            <p><strong>Room:</strong> {{ booking.room_number }}</p>
            <p><strong>Guests:</strong> {{ booking.number_of_guests }}</p>
          </el-col>
        </el-row>
      </el-card>

      <!-- Check-in Form -->
      <el-form ref="form" :model="form" :rules="rules" label-width="140px" class="mt-3">
        <el-form-item label="ID Type" prop="id_type">
          <el-select v-model="form.id_type" placeholder="Select ID type">
            <el-option label="Passport" value="passport"></el-option>
            <el-option label="Driver's License" value="drivers_license"></el-option>
            <el-option label="National ID" value="national_id"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="ID Number" prop="id_number">
          <el-input v-model="form.id_number" placeholder="Enter ID number"></el-input>
        </el-form-item>

        <el-form-item label="Vehicle Number">
          <el-input v-model="form.vehicle_number" placeholder="If applicable"></el-input>
        </el-form-item>

        <el-form-item label="Arrival Time">
          <el-time-picker
            v-model="form.arrival_time"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="Select time"
          ></el-time-picker>
        </el-form-item>

        <el-form-item label="Additional Notes">
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="3"
            placeholder="Any additional notes"
          ></el-input>
        </el-form-item>

        <el-form-item label="Room Condition">
          <el-checkbox-group v-model="form.room_condition">
            <el-checkbox label="clean">Clean</el-checkbox>
            <el-checkbox label="keys_provided">Keys Provided</el-checkbox>
            <el-checkbox label="amenities_working">All Amenities Working</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>

      <!-- Payment Summary -->
      <el-card class="info-card mt-3">
        <div slot="header">
          <span>Payment Summary</span>
        </div>
        <el-row>
          <el-col :span="12">
            <p><strong>Total Amount:</strong> ${{ booking.total_amount.toFixed(2) }}</p>
            <p><strong>Paid Amount:</strong> ${{ booking.paid_amount.toFixed(2) }}</p>
          </el-col>
          <el-col :span="12">
            <p><strong>Balance Due:</strong>
              <span :class="{'text-danger': balanceDue > 0}">
                ${{ balanceDue.toFixed(2) }}
              </span>
            </p>
          </el-col>
        </el-row>

        <el-form-item label="Collect Payment" v-if="balanceDue > 0" class="mt-3">
          <el-input-number
            v-model="form.payment_amount"
            :min="0"
            :max="balanceDue"
            :precision="2"
            controls-position="right"
          >
            <template slot="prepend">$</template>
          </el-input-number>
          <el-select v-model="form.payment_method" placeholder="Payment method" style="margin-left: 10px">
            <el-option label="Cash" value="cash"></el-option>
            <el-option label="Credit Card" value="credit_card"></el-option>
            <el-option label="Debit Card" value="debit_card"></el-option>
          </el-select>
        </el-form-item>
      </el-card>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="handleCheckIn" :loading="loading">
        Complete Check-In
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import bookingService from '@/services/booking.service'

export default {
  name: 'CheckInDialog',
  props: {
    visible: Boolean,
    booking: Object
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      form: {
        id_type: '',
        id_number: '',
        vehicle_number: '',
        arrival_time: new Date().toTimeString().slice(0, 5),
        notes: '',
        room_condition: ['clean', 'keys_provided', 'amenities_working'],
        payment_amount: 0,
        payment_method: 'cash'
      },
      rules: {
        id_type: [
          { required: true, message: 'ID type is required', trigger: 'change' }
        ],
        id_number: [
          { required: true, message: 'ID number is required', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    balanceDue () {
      if (this.booking) {
        return this.booking.total_amount - this.booking.paid_amount
      }
      return 0
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val) {
        this.initializeForm()
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    initializeForm () {
      this.form.payment_amount = this.balanceDue
    },

    handleClose () {
      this.$refs.form.resetFields()
      this.dialogVisible = false
    },

    async handleCheckIn () {
      try {
        await this.$refs.form.validate()
        this.loading = true

        const checkInData = {
          ...this.form,
          actual_check_in_time: new Date().toISOString()
        }

        // Process check-in
        await bookingService.checkIn(this.booking.id, checkInData)

        // Process payment if any
        if (this.form.payment_amount > 0) {
          await bookingService.addPayment(this.booking.id, {
            amount: this.form.payment_amount,
            payment_method: this.form.payment_method
          })
        }

        this.$emit('checked-in')
        this.handleClose()
      } catch (error) {
        if (error !== false) { // Validation error
          this.$message.error('Failed to check in guest')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.info-card {
  margin-bottom: 20px;
}

.info-card p {
  margin: 8px 0;
  color: #606266;
}

.mt-3 {
  margin-top: 20px;
}

.text-danger {
  color: #F56C6C;
  font-weight: bold;
}
</style>
